import { useCallback, useMemo, useState } from 'react';
import { useLocalStorageByUser } from 'common/hooks';

export type EmailTemplatePresetBase = {
  id: string;
  label: string;
  emailSubject: string;
  introduction: string;
};

export type StoredEmailTemplatePreset<T extends EmailTemplatePresetBase> = {
  id: number;
  name: string;
  template: T;
};

export type UseEmailTemplatePresetsOptions<T extends EmailTemplatePresetBase> = {
  // Preference key for the active (last-edited) template draft.
  activeKey: string;
  // Built-in templates that always appear in the selector.
  defaults: T[];
  // Fields copied when saving a custom preset from the active draft.
  pickPresetFields: (template: T) => T;
  // Server-backed named presets for the current user.
  presets: StoredEmailTemplatePreset<T>[];
  onSavePreset: (name: string, template: T, existingId?: number) => Promise<void> | void;
  onDeletePreset: (name: string, presetId: number) => Promise<void> | void;
  onUpdatePreset?: (name: string, template: T, presetId: number) => Promise<void> | void;
};

/**
 * Per-user email template presets UI state. Named presets are persisted via the
 * caller (server); active default-template drafts stay in local storage.
 */
export const useEmailTemplatePresets = <T extends EmailTemplatePresetBase>({
  activeKey,
  defaults,
  pickPresetFields,
  presets,
  onSavePreset,
  onDeletePreset,
  onUpdatePreset,
}: UseEmailTemplatePresetsOptions<T>) => {
  const defaultTemplate = defaults[0];
  const [storedActive, setActiveTemplate] = useLocalStorageByUser<T | null>(activeKey, null);
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [selectedId, setSelectedId] = useState(defaultTemplate?.id ?? '');

  const activeTemplate = useMemo(() => {
    if (storedActive?.id && storedActive.introduction !== undefined) {
      const matchedDefault =
        defaults.find(template => template.id === storedActive.id) ?? defaultTemplate;
      return {
        ...matchedDefault,
        introduction: storedActive.introduction,
      } as T;
    }
    return defaultTemplate;
  }, [storedActive, defaultTemplate, defaults]);

  const templates = useMemo(() => {
    const presetTemplates = presets.map(preset => ({
      ...preset.template,
      id: `preset:${preset.name}`,
      label: preset.name,
    }));
    return [...defaults, ...presetTemplates];
  }, [defaults, presets]);

  const selectedTemplate = useMemo(() => {
    if (selectedId.startsWith('preset:')) {
      const name = selectedId.slice('preset:'.length);
      const preset = presets.find(item => item.name === name);
      if (preset) {
        return {
          ...preset.template,
          id: selectedId,
          label: preset.name,
        } as T;
      }
    }
    const fromDefaults = defaults.find(template => template.id === selectedId);
    if (fromDefaults) {
      if (activeTemplate?.id === fromDefaults.id) {
        return { ...fromDefaults, ...activeTemplate, id: fromDefaults.id, label: fromDefaults.label };
      }
      return fromDefaults;
    }
    return activeTemplate ?? defaultTemplate;
  }, [selectedId, presets, defaults, activeTemplate, defaultTemplate]);

  const updateSelectedTemplate = useCallback(
    (patch: Partial<T>) => {
      const base = selectedTemplate ?? defaultTemplate;
      if (!base) {
        return;
      }
      const next = { ...base, ...patch } as T;
      if (!selectedId.startsWith('preset:')) {
        setActiveTemplate(next);
        setSelectedId(next.id);
        return;
      }
      const name = selectedId.slice('preset:'.length);
      const preset = presets.find(item => item.name === name);
      const updatedTemplate = pickPresetFields({
        ...(preset?.template ?? base),
        ...patch,
        id: preset?.template.id ?? base.id,
        label: name,
      });
      onUpdatePreset?.(name, updatedTemplate, preset?.id as number);
    },
    [selectedTemplate, defaultTemplate, selectedId, setActiveTemplate, presets, pickPresetFields, onUpdatePreset]
  );

  const handleSelectTemplateId = useCallback(
    (id: string) => {
      setSelectedId(id);
      if (!id.startsWith('preset:')) {
        const match = defaults.find(template => template.id === id);
        if (match) {
          setActiveTemplate(match);
        }
      }
    },
    [defaults, setActiveTemplate]
  );

  const handleOpenSavePreset = useCallback(() => {
    setSavePresetOpen(true);
  }, []);

  const handleCloseSavePreset = useCallback(() => {
    setSavePresetOpen(false);
    setPresetName('');
  }, []);

  const handleSavePreset = useCallback(async () => {
    const name = presetName.trim();
    const source = selectedTemplate ?? defaultTemplate;
    if (!name || !source) {
      return;
    }
    const template = pickPresetFields({
      ...source,
      id: `custom-${Date.now()}`,
      label: name,
    });
    const existing = presets.find(item => item.name === name);
    await onSavePreset(name, template, existing?.id);
    setSelectedId(`preset:${name}`);
    setPresetName('');
    setSavePresetOpen(false);
  }, [presetName, selectedTemplate, defaultTemplate, pickPresetFields, presets, onSavePreset]);

  const handleApplyPreset = useCallback((name: string) => {
    setSelectedId(`preset:${name}`);
  }, []);

  const handleDeletePreset = useCallback(
    async (name: string) => {
      const preset = presets.find(item => item.name === name);
      if (!preset) {
        return;
      }
      await onDeletePreset(name, preset.id);
      if (selectedId === `preset:${name}`) {
        const fallbackId = defaultTemplate?.id ?? '';
        setSelectedId(fallbackId);
        if (defaultTemplate) {
          setActiveTemplate(defaultTemplate);
        }
      }
    },
    [selectedId, defaultTemplate, presets, onDeletePreset, setActiveTemplate]
  );

  const handleResetToDefault = useCallback(() => {
    if (!defaultTemplate) {
      return;
    }
    setActiveTemplate(defaultTemplate);
    setSelectedId(defaultTemplate.id);
  }, [defaultTemplate, setActiveTemplate]);

  const presetSummaries = useMemo(
    () => presets.map(preset => ({ name: preset.name })),
    [presets]
  );

  return {
    templates,
    presets: presetSummaries,
    selectedId,
    selectedTemplate,
    setSelectedId: handleSelectTemplateId,
    updateSelectedTemplate,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
  };
};

export default useEmailTemplatePresets;
